import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import type { MultiValue } from 'react-select'
import type { StaticChipsProps, LookupChipsProps, SelectOption } from './Select.types'
import {
  Label,
  buildClassNames,
  selectLayoutStyles,
  DropdownIndicator,
  ClearIndicator,
  IndicatorsContainer,
  RdControl,
  MenuListWithSearch,
  ChipsOption,
  LoadingIndicator,
  fetchLookupPage,
} from './Select.utils'

type Props = StaticChipsProps | LookupChipsProps

export default function SelectChips(props: Props) {
  const { name, label, placeholder, required, disabled, floating, size = 'medium', error, helpText, onChange, setterRef } = props

  const isLookup = props.mode === 'lookup-chips'
  const creatable = props.mode === 'lookup-chips' && !!props.creatable

  const [isOpen, setIsOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [selected, setSelected] = useState<SelectOption[]>(() => {
    if (props.mode === 'lookup-chips') return props.initialValues ?? []
    return (props.initialValues ?? []).map(
      v => props.options.find(o => o.value === v) ?? { value: v, label: v },
    )
  })

  // Lookup-only state
  const [options, setOptions] = useState<SelectOption[]>(() =>
    props.mode === 'static-chips' ? props.options : [],
  )
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  // Объекты, созданные через попап
  const [extraOptions, setExtraOptions] = useState<SelectOption[]>([])
  const pageRef = useRef(1)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!setterRef) return
    setterRef.current = (value: string, optionLabel?: string) => {
      if (!value) return
      const option = { value, label: optionLabel ?? value }
      const append = (prev: SelectOption[]) =>
        prev.some(existing => existing.value === value) ? prev : [...prev, option]
      setExtraOptions(append)
      setSelected(append)
    }
    return () => {
      setterRef.current = null
    }
  }, [setterRef])

  const load = useCallback(
    async (q: string, page: number) => {
      if (!isLookup) return
      const lookupProps = props as LookupChipsProps
      setIsLoading(true)
      try {
        const { options: next, hasMore: more } = await fetchLookupPage(
          lookupProps.lookupUrl,
          q,
          page,
          lookupProps.pageSize ?? 100,
          lookupProps.idField,
        )
        setOptions(prev => (page === 1 ? next : [...prev, ...next]))
        setHasMore(more)
      } finally {
        setIsLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLookup, (props as LookupChipsProps).lookupUrl, (props as LookupChipsProps).pageSize],
  )

  const handleSearchChange = useCallback(
    (q: string) => {
      setSearchQuery(q)
      if (!isLookup) return
      if (timerRef.current !== null) clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => {
        pageRef.current = 1
        load(q, 1)
      }, 300)
    },
    [isLookup, load],
  )

  const handleMenuOpen = useCallback(() => {
    setIsOpen(true)
    if (isLookup) {
      pageRef.current = 1
      load('', 1)
    }
  }, [isLookup, load])

  const closeMenu = useCallback(() => {
    setIsOpen(false)
    setSearchQuery('')
    if (isLookup) setOptions([])
  }, [isLookup])

  // Закрывать меню при клике вне компонента — react-select не видит клик снаружи
  // когда фокус находится на нашем кастомном инпуте поиска, а не на его скрытом инпуте.
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeMenu()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, closeMenu])

  const handleSearchMouseDown = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation()
  }, [])

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') closeMenu()
  }, [closeMenu])

  const loadMore = useCallback(() => {
    if (isLoading) return
    pageRef.current += 1
    load(searchQuery, pageRef.current)
  }, [searchQuery, load, isLoading])

  // Стабильная ссылка — react-select перемонтирует меню при каждом изменении объекта components
  const selectComponents = useMemo(() => ({
    DropdownIndicator, ClearIndicator, IndicatorsContainer, Control: RdControl,
    MenuList: MenuListWithSearch as any, Option: ChipsOption, LoadingIndicator,
  }), [])

  const handleChange = (opts: MultiValue<SelectOption>) => {
    const next = [...opts]
    setSelected(next)
    ;(onChange as ((v: string[], o: SelectOption[]) => void) | undefined)?.(next.map(o => o.value), next)
  }

  const createValue = useMemo(() => {
    if (!creatable) return null
    const trimmed = searchQuery.trim()
    if (!trimmed) return null
    const lower = trimmed.toLowerCase()
    if (options.some(o => o.label.toLowerCase() === lower)) return null
    if (selected.some(o => o.label.toLowerCase() === lower)) return null
    return trimmed
  }, [creatable, searchQuery, options, selected])

  // For static mode, filter client-side by searchQuery; for lookup, server-side (filterOption=null)
  const menuOptions = React.useMemo(() => {
    const withExtras = (baseOptions: SelectOption[]) =>
      extraOptions.length
        ? [
            ...baseOptions,
            ...extraOptions.filter(extra => !baseOptions.some(option => option.value === extra.value)),
          ]
        : baseOptions
    if (props.mode !== 'static-chips') {
      const merged = withExtras(options)
      return createValue ? [{ value: createValue, label: createValue }, ...merged] : merged
    }
    const baseOptions = withExtras(props.options)
    const query = searchQuery.toLowerCase()
    if (!query) return baseOptions
    return baseOptions.filter(option => option.label.toLowerCase().includes(query))
  }, [props, options, searchQuery, createValue, extraOptions])

  return (
    <>
      {label && !floating && <Label text={label} required={required} />}
      <div ref={containerRef} style={{ display: 'contents' }}>
        <Select<SelectOption, true>
          unstyled
          isMulti
          isSearchable={false}
          menuIsOpen={isOpen}
          options={menuOptions}
          value={selected}
          placeholder={floating ? '' : (placeholder ?? '')}
          isDisabled={disabled}
          isClearable={selected.length > 0}
          filterOption={null}
          hideSelectedOptions={false}
          closeMenuOnSelect={false}
          menuPlacement="auto"
          minMenuHeight={300}
          onMenuOpen={handleMenuOpen}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          classNames={buildClassNames({ size, floating, error, isMulti: true, isOpen, isFocused })}
          styles={selectLayoutStyles}
          components={selectComponents}
          floatingLabel={floating ? label : undefined}
          floatingRequired={floating ? required : undefined}
          menuSearchQuery={searchQuery}
          onMenuSearchChange={handleSearchChange}
          onMenuSearchMouseDown={handleSearchMouseDown}
          onMenuSearchKeyDown={handleSearchKeyDown}
          menuHasMore={hasMore}
          onMenuLoadMore={loadMore}
          loadingMessage={() => null}
          noOptionsMessage={() => 'Нет результатов'}
        />
      </div>

      {error && <span className="rd-dropdown__error-text">{error}</span>}
      {helpText && <span className="rd-dropdown__helper-text">{helpText}</span>}

      {/* Form submission — multiple hidden inputs for Django getlist() */}
      {selected.map(opt => (
        <input key={opt.value} type="hidden" name={name} value={opt.value} />
      ))}
    </>
  )
}
